import { fbt } from 'fbtee'
import {
  CalendarCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  CircleDot,
  Clock,
  Flag,
  Hourglass,
  Lightbulb,
  MapPinPlus,
  MessageSquare,
  MinusCircle,
  X,
  type LucideIcon,
} from 'lucide-react'
import { regionForFreeText } from '@shared/regions'
import type { RequestItem, RequestKind } from '../../hooks/useMyRequests'

export interface RequestDisplay {
  label: string
  icon: LucideIcon
  /** Tailwind text colour for the icon + status label. */
  tone: string
}

export const KIND_ICON: Record<RequestKind, LucideIcon> = {
  suggestion: Lightbulb,
  lead: CalendarClock,
  feedback: MessageSquare,
  region: MapPinPlus,
  setReport: Flag,
  eventReport: Flag,
}

export function requestKindLabel(kind: RequestKind): string {
  switch (kind) {
    case 'suggestion':
      return fbt('Event suggestion', 'Kind label on a My requests row for a suggested event')
    case 'lead':
      return fbt('Set times', 'Kind label on a My requests row for a set-times report')
    case 'feedback':
      return fbt('Feedback', 'Kind label on a My requests row for a feedback submission')
    case 'region':
      return fbt('Area request', 'Kind label on a My requests row for a requested area')
    case 'setReport':
      return fbt('Set problem', 'Kind label on a My requests row for a problem reported on one set')
    case 'eventReport':
      return fbt('Event problem', "Kind label on a My requests row for a problem reported on an event's listing")
  }
}

// Shared across kinds; functions rather than constants so fbt resolves in the
// active locale at render time.
function pending(): RequestDisplay {
  return {
    label: fbt('Pending', 'Status on a My requests row for a request nobody has looked at yet'),
    icon: Clock,
    tone: 'text-primary',
  }
}
function couldntAdd(): RequestDisplay {
  return {
    label: fbt("Couldn't add", 'Status on a My requests row for a request we tried and failed to fulfil'),
    icon: X,
    tone: 'text-destructive',
  }
}
function notAdded(): RequestDisplay {
  return {
    label: fbt('Not added', 'Status on a My requests row for a request judged not actionable'),
    icon: MinusCircle,
    tone: 'text-muted-foreground',
  }
}

function suggestionDisplay(item: Extract<RequestItem, { kind: 'suggestion' }>): RequestDisplay {
  switch (item.doc.status) {
    case 'seeded':
    case 'multi':
      return {
        label: fbt('Added', 'Status on a My requests row for a suggested event that is now in Beacon'),
        icon: Check,
        tone: 'text-success',
      }
    case 'failed':
      return couldntAdd()
    case 'noise':
      return notAdded()
    default:
      return item.doc.eventIds?.length
        ? {
            label: fbt('In review', 'Status on a My requests row for a suggested event that was scraped and awaits publishing'),
            icon: Hourglass,
            tone: 'text-warning',
          }
        : pending()
  }
}

function leadDisplay(item: Extract<RequestItem, { kind: 'lead' }>): RequestDisplay {
  switch (item.doc.status) {
    case 'seeded':
      return {
        label: fbt('Set times added', 'Status on a My requests row once the reported set times are in the schedule'),
        icon: Check,
        tone: 'text-success',
      }
    case 'failed':
      return couldntAdd()
    case 'noise':
      return notAdded()
    default:
      return pending()
  }
}

function feedbackDisplay(item: Extract<RequestItem, { kind: 'feedback' }>): RequestDisplay {
  if (item.doc.addressed) {
    return {
      label: fbt('Addressed', 'Status on a My requests row once the fix for the feedback has shipped'),
      icon: CheckCircle2,
      tone: 'text-success',
    }
  }
  switch (item.doc.status) {
    case 'issue_created':
    case 'duplicate':
      return {
        label: fbt('In progress', 'Status on a My requests row for feedback that has been triaged into work'),
        icon: CircleDot,
        tone: 'text-sky-500',
      }
    case 'noise':
      return {
        label: fbt('Closed', 'Status on a My requests row for feedback closed without action'),
        icon: MinusCircle,
        tone: 'text-muted-foreground',
      }
    default:
      return {
        label: fbt('Received', 'Status on a My requests row for feedback nobody has triaged yet'),
        icon: Clock,
        tone: 'text-primary',
      }
  }
}

/** Region id when the requested area is now one we cover, whatever its status says. */
export function coveredRegionId(item: Extract<RequestItem, { kind: 'region' }>): string | undefined {
  if (item.doc.status === 'dismissed') return undefined
  return regionForFreeText(item.doc.text)
}

// "Now covered" is derived at read time and deliberately outranks `planned`:
// once the city is in shared/regions.ts, that's the more useful truth.
function regionDisplay(item: Extract<RequestItem, { kind: 'region' }>): RequestDisplay {
  if (coveredRegionId(item)) {
    return {
      label: fbt('Now covered', 'Status on a My requests row for a requested area Beacon now covers'),
      icon: Check,
      tone: 'text-success',
    }
  }
  switch (item.doc.status) {
    case 'planned':
      return {
        label: fbt('Planned', 'Status on a My requests row for a requested area we intend to add'),
        icon: CalendarCheck,
        tone: 'text-success',
      }
    case 'dismissed':
      return {
        label: fbt('Not planned', 'Status on a My requests row for a requested area we decided not to add'),
        icon: MinusCircle,
        tone: 'text-muted-foreground',
      }
    default:
      return pending()
  }
}

// A report the source didn't bear out closes as dismissed; to the reporter
// that reads as "checked", not as being brushed off.
function reportDisplay(item: Extract<RequestItem, { kind: 'setReport' | 'eventReport' }>): RequestDisplay {
  switch (item.doc.status) {
    case 'fixed':
      return {
        label: fbt('Fixed', 'Status on a My requests row once the reported set problem is corrected'),
        icon: Check,
        tone: 'text-success',
      }
    case 'dismissed':
      return {
        label: fbt('Checked, no change', 'Status on a My requests row for a set report the source did not bear out'),
        icon: MinusCircle,
        tone: 'text-muted-foreground',
      }
    default:
      return pending()
  }
}

export function requestDisplay(item: RequestItem): RequestDisplay {
  switch (item.kind) {
    case 'suggestion':
      return suggestionDisplay(item)
    case 'lead':
      return leadDisplay(item)
    case 'feedback':
      return feedbackDisplay(item)
    case 'region':
      return regionDisplay(item)
    case 'setReport':
    case 'eventReport':
      return reportDisplay(item)
  }
}
